// SCR-08 카카오톡 상담 관제 /chat — 2단 레이아웃(스레드 리스트 + 대화창), 5초 폴링
// 모바일: 리스트↔대화창 전환(선택 시 대화창 풀스크린) · ?client= 딥링크 수신
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChatCircleDots } from '@phosphor-icons/react'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../components/Toast'
import { useChatThreads, usePendingContacts } from './api'
import { ChatRoom } from './ChatRoom'
import { ThreadList, type ThreadFilter } from './ThreadList'

export function ChatPage() {
  const [searchParams] = useSearchParams()
  const clientParam = searchParams.get('client')
  const { showToast } = useToast()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search)
  const [filter, setFilter] = useState<ThreadFilter>('ALL')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: threads = [], isLoading } = useChatThreads(debouncedSearch)
  const { data: pendingContacts = [], isLoading: pendingLoading } = usePendingContacts()

  // ?client=<id> 딥링크 — 해당 고객사 최신 스레드 자동 선택 (1회)
  const appliedClientRef = useRef<string | null>(null)
  useEffect(() => {
    if (!clientParam || threads.length === 0) return
    if (appliedClientRef.current === clientParam) return
    appliedClientRef.current = clientParam
    const target = threads.find((t) => t.client_id === clientParam)
    if (target) {
      setSelectedId(target.thread_id)
    } else {
      showToast('해당 고객사의 상담 스레드가 아직 없습니다.', 'info')
    }
  }, [clientParam, threads, showToast])

  const selected = useMemo(
    () => threads.find((t) => t.thread_id === selectedId) ?? null,
    [threads, selectedId],
  )

  return (
    <div className="animate-fade-in -mx-4 -mt-5 -mb-24 flex h-[calc(100dvh-4rem-3.75rem)] overflow-hidden bg-void lg:-mx-6 lg:-mb-6 lg:h-[calc(100dvh-4rem)]">
      {/* 좌측: 신규 문의(미매핑) 섹션 + 고객사별 그룹 상담 목록 */}
      <div
        className={`w-full flex-col border-r border-hairline bg-void md:flex md:w-80 lg:w-96 ${
          selected ? 'hidden' : 'flex'
        }`}
      >
        <ThreadList
          threads={threads}
          isLoading={isLoading}
          search={search}
          onSearchChange={setSearch}
          filter={filter}
          onFilterChange={setFilter}
          selectedId={selectedId}
          onSelect={setSelectedId}
          pendingContacts={pendingContacts}
          pendingLoading={pendingLoading}
        />
      </div>

      {/* 우측: 대화창 (모바일은 선택 시 풀스크린) */}
      <div className={`min-w-0 flex-1 md:flex ${selected ? 'flex' : 'hidden'}`}>
        {selected ? (
          <ChatRoom thread={selected} onBack={() => setSelectedId(null)} />
        ) : (
          <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 bg-void text-slatey">
            <ChatCircleDots size={40} />
            <p className="text-sm font-medium">좌측 목록에서 상담을 선택하세요</p>
            <p className="text-xs">고객 카카오톡 문의가 5초 간격으로 자동 갱신됩니다.</p>
          </div>
        )}
      </div>
    </div>
  )
}
